import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { DataRoomTeaser } from '../../shared/models';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';
import { QueryKeys } from '../../shared/hooks';

interface ToggleReferralLanguageDialogProps {
	dataRoomGuid: string | undefined;
	teaser: DataRoomTeaser | null;
	close: () => void;
}

export const ToggleReferralLanguageDialog: React.FC<ToggleReferralLanguageDialogProps> = ({
	dataRoomGuid,
	teaser,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const title = teaser?.includeReferralLanguage
		? 'Confirm Exclude Referral Language'
		: 'Confirm Include Referral Language';
	const bodyText = teaser?.includeReferralLanguage
		? 'exclude the referral language'
		: 'include the referral language';

	const onConfirmation = () => {
		if (teaser !== null) confirmAction(teaser);
	};

	const { mutate: confirmAction } = useMutation<DataRoomTeaser, unknown, DataRoomTeaser>(
		teaserToConfirm =>
			getResponseData(
				api.post<DataRoomTeaser>(
					CerebroApiEndpoints.Teasers.ReferralLanguage(dataRoomGuid, teaserToConfirm.guid)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: (_data, resetTeaser) => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Referral language setting confirmed.', { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem confirming the referral language setting.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={teaser !== null} onClose={close}>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to {bodyText} for {teaser?.firstName} {teaser?.lastName} at {teaser?.bankName}
					?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
